/**
 * 🛡️ G2eGente - Teste DISC do Candidato (Web)
 * Idêntico ao app mobile
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CandidateLayout from '../../components/CandidateLayout';
import { candidatesAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { 
  Loader2,
  Brain,
  ChevronLeft,
  ChevronRight,
  Target,
  Users,
  Heart,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

// 20 perguntas DISC com escala Likert
const DISC_QUESTIONS = [
  { id: 1, text: "Gosto de assumir a liderança em projetos de equipe.", trait: "D" },
  { id: 2, text: "Prefiro tomar decisões rapidamente em vez de esperar.", trait: "D" },
  { id: 3, text: "Sou competitivo e gosto de vencer desafios.", trait: "D" },
  { id: 4, text: "Vou direto ao ponto em conversas e reuniões.", trait: "D" },
  { id: 5, text: "Sinto-me confortável confrontando problemas de frente.", trait: "D" },
  { id: 6, text: "Gosto de conhecer novas pessoas e fazer networking.", trait: "I" },
  { id: 7, text: "Sou otimista e consigo motivar os outros facilmente.", trait: "I" },
  { id: 8, text: "Prefiro trabalhar em equipe do que sozinho.", trait: "I" },
  { id: 9, text: "Sou expressivo e demonstro minhas emoções abertamente.", trait: "I" },
  { id: 10, text: "Gosto de ambientes de trabalho descontraídos e divertidos.", trait: "I" },
  { id: 11, text: "Prefiro rotinas previsíveis a mudanças constantes.", trait: "S" },
  { id: 12, text: "Sou paciente e sei esperar o momento certo.", trait: "S" },
  { id: 13, text: "Valorizo a harmonia e evito conflitos desnecessários.", trait: "S" },
  { id: 14, text: "Sou leal às pessoas e empresas em que confio.", trait: "S" },
  { id: 15, text: "Prefiro ouvir mais do que falar em discussões.", trait: "S" },
  { id: 16, text: "Analiso os detalhes antes de tomar decisões importantes.", trait: "C" },
  { id: 17, text: "Busco sempre a precisão e qualidade no meu trabalho.", trait: "C" },
  { id: 18, text: "Prefiro seguir processos e métodos estabelecidos.", trait: "C" },
  { id: 19, text: "Sou organizado e gosto de planejar antecipadamente.", trait: "C" },
  { id: 20, text: "Questiono informações e busco dados para validar ideias.", trait: "C" }
];

const LIKERT_SCALE = [
  { value: 1, label: "Discordo totalmente" },
  { value: 2, label: "Discordo" },
  { value: 3, label: "Neutro" },
  { value: 4, label: "Concordo" },
  { value: 5, label: "Concordo totalmente" }
];

const DISC_PROFILES = {
  D: {
    name: "D - Dominância",
    title: "Determinado",
    icon: Target,
    color: "text-red-500",
    bgColor: "bg-red-100",
    description: "Você é orientado a resultados, direto e decisivo. Gosta de desafios e toma ações rápidas para alcançar objetivos.",
    strengths: ["Liderança", "Foco em resultados", "Tomada de decisão", "Resolução de problemas"],
    attention: ["Impaciente às vezes", "Pode ser muito direto"],
    tips: "Lembre-se de ouvir os outros e considerar diferentes perspectivas."
  },
  I: {
    name: "I - Influência",
    title: "Inspirador",
    icon: Users,
    color: "text-amber-500",
    bgColor: "bg-amber-100",
    description: "Você é entusiasta, otimista e colaborativo. Gosta de trabalhar com pessoas e criar um ambiente positivo.",
    strengths: ["Comunicação", "Entusiasmo", "Criatividade", "Trabalho em equipe"],
    attention: ["Pode perder foco", "Otimismo excessivo às vezes"],
    tips: "Mantenha o foco nas tarefas e prazos para maximizar sua eficácia."
  },
  S: {
    name: "S - Estabilidade",
    title: "Solidário",
    icon: Heart,
    color: "text-emerald-500",
    bgColor: "bg-emerald-100",
    description: "Você é paciente, confiável e cooperativo. Valoriza a harmonia e oferece suporte consistente à equipe.",
    strengths: ["Paciência", "Confiabilidade", "Escuta ativa", "Colaboração"],
    attention: ["Resistência a mudanças", "Dificuldade em dizer não"],
    tips: "Não tenha medo de expressar suas opiniões e necessidades."
  },
  C: {
    name: "C - Conformidade",
    title: "Consciencioso",
    icon: Shield,
    color: "text-blue-500",
    bgColor: "bg-blue-100",
    description: "Você é analítico, preciso e focado em qualidade. Valoriza a precisão e segue padrões elevados.",
    strengths: ["Análise", "Precisão", "Organização", "Qualidade"],
    attention: ["Perfeccionismo", "Análise excessiva às vezes"],
    tips: "Aceite que nem tudo precisa ser perfeito para funcionar bem."
  }
};

export default function CandidateDISC() {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const totalQuestions = DISC_QUESTIONS.length;
  const progress = ((currentQuestion + (result ? 1 : 0)) / totalQuestions) * 100;
  const currentAnswer = answers[currentQuestion];

  const handleSelectAnswer = (value) => {
    setAnswers({ ...answers, [currentQuestion]: value });
  };

  const handleNext = () => {
    if (currentQuestion < totalQuestions - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      calculateResult();
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const calculateResult = () => {
    const scores = { D: 0, I: 0, S: 0, C: 0 };
    
    DISC_QUESTIONS.forEach((q, index) => {
      const answer = answers[index] || 3;
      scores[q.trait] += answer;
    });
    
    const sortedTraits = Object.entries(scores).sort((a, b) => b[1] - a[1]);
    const dominant = sortedTraits[0][0];
    const secondary = sortedTraits[1][0];
    
    const maxScore = 25;
    const dominantPercentage = Math.round((scores[dominant] / maxScore) * 100);
    
    setResult({
      dominant,
      secondary,
      scores,
      aderencia: dominantPercentage
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const profile = DISC_PROFILES[result.dominant];
      const discDetalhado = {
        resultado_dominante: `${result.dominant}${result.secondary}`,
        aderencia_cargo: result.aderencia,
        texto_explicativo: profile.description,
        pontos_fortes: profile.strengths,
        pontos_atencao: profile.attention
      };
      
      await candidatesAPI.updateMe({ 
        disc_profile: `${result.dominant}${result.secondary}`,
        disc_detalhado: discDetalhado
      });
      toast.success('Perfil DISC salvo com sucesso!');
      navigate('/candidate/profile');
    } catch (error) {
      toast.error('Erro ao salvar perfil DISC');
    } finally {
      setSaving(false);
    }
  };

  const handleRetake = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setResult(null);
  };

  // Result Screen
  if (result) {
    const profile = DISC_PROFILES[result.dominant];
    const secondaryProfile = DISC_PROFILES[result.secondary];
    const ProfileIcon = profile.icon;

    return (
      <CandidateLayout>
        <div className="max-w-2xl mx-auto space-y-6 pb-20 lg:pb-0" data-testid="disc-result">
          {/* Result Header */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 text-center">
            <div className={`w-24 h-24 ${profile.bgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
              <ProfileIcon className={`w-12 h-12 ${profile.color}`} />
            </div>
            <h2 className="text-2xl font-bold text-white">{profile.title}</h2>
            <p className={`text-lg ${profile.color}`}>{profile.name}</p>
            <div className="mt-3 flex items-center justify-center gap-2">
              <span className="text-slate-400 text-sm">Perfil secundário:</span>
              <span className={`${secondaryProfile.color} font-medium`}>{secondaryProfile.name}</span>
            </div>
            <div className="mt-4 bg-white/10 rounded-full px-4 py-2 inline-block">
              <span className="text-white text-sm">Aderência: </span>
              <span className="text-cyan-400 font-bold">{result.aderencia}%</span>
            </div>
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h3 className="font-semibold text-slate-900 mb-2">Sobre você</h3>
              <p className="text-slate-600 leading-relaxed">{profile.description}</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Seus Pontos Fortes</h3>
              <div className="flex flex-wrap gap-2">
                {profile.strengths.map((strength, i) => (
                  <span 
                    key={i}
                    className={`px-4 py-2 rounded-full text-sm ${profile.bgColor} ${profile.color}`}
                  >
                    {strength}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Pontos de Atenção</h3>
              <div className="flex flex-wrap gap-2">
                {profile.attention.map((item, i) => (
                  <span 
                    key={i}
                    className="px-4 py-2 rounded-full text-sm bg-amber-100 text-amber-700"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-cyan-200 bg-cyan-50">
            <CardContent className="p-6">
              <h3 className="font-semibold text-cyan-900 mb-2">Dica de Desenvolvimento</h3>
              <p className="text-cyan-700">{profile.tips}</p>
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button 
              variant="outline"
              className="flex-1"
              onClick={handleRetake}
            >
              Refazer Teste
            </Button>
            <Button 
              className="flex-1 bg-cyan-600 hover:bg-cyan-700"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Salvar Perfil'}
            </Button>
          </div>
        </div>
      </CandidateLayout>
    );
  }

  // Question Screen
  const question = DISC_QUESTIONS[currentQuestion];

  return (
    <CandidateLayout>
      <div className="max-w-2xl mx-auto space-y-6 pb-20 lg:pb-0" data-testid="disc-quiz">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
            <Brain className="w-6 h-6 text-purple-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-slate-900">Teste DISC</h1>
            <p className="text-sm text-slate-600">Pergunta {currentQuestion + 1} de {totalQuestions}</p>
          </div>
        </div>

        <Progress value={progress} className="h-2" />

        {/* Question */}
        <Card className="border-slate-200">
          <CardContent className="p-8">
            <p className="text-xl font-medium text-slate-900 text-center leading-relaxed">
              "{question.text}"
            </p>
          </CardContent>
        </Card>

        {/* Likert Scale */}
        <div className="space-y-3">
          <p className="text-sm text-slate-500 text-center mb-4">
            O quanto você concorda com esta afirmação?
          </p>
          
          {LIKERT_SCALE.map((option) => (
            <button
              key={option.value}
              onClick={() => handleSelectAnswer(option.value)}
              className={`w-full p-4 rounded-xl text-left transition-all ${
                currentAnswer === option.value
                  ? 'bg-cyan-600 text-white shadow-lg scale-[1.02]'
                  : 'bg-white border border-slate-200 hover:border-cyan-500 hover:bg-cyan-50'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                  currentAnswer === option.value
                    ? 'bg-white/20 text-white'
                    : 'bg-slate-100 text-slate-600'
                }`}>
                  {option.value}
                </div>
                <span className={`font-medium ${
                  currentAnswer === option.value ? 'text-white' : 'text-slate-900'
                }`}>
                  {option.label}
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            className="flex-1"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Anterior
          </Button>
          <Button
            onClick={handleNext}
            disabled={currentAnswer === undefined}
            className="flex-1 bg-cyan-600 hover:bg-cyan-700"
          >
            {currentQuestion === totalQuestions - 1 ? 'Ver Resultado' : 'Próximo'}
            {currentQuestion < totalQuestions - 1 && <ChevronRight className="w-5 h-5 ml-1" />}
          </Button>
        </div>

        <p className="text-center text-sm text-slate-500">
          {Object.keys(answers).length} de {totalQuestions} respondidas
        </p>
      </div>
    </CandidateLayout>
  );
}
